export type VariantFrontmatter = {
  variant: string
  name: string
  description?: string
}

// Tiny YAML-frontmatter splitter — only handles flat string fields, which is
// all variant md files use. Avoids pulling gray-matter into the browser bundle.
function splitFrontmatter(source: string): { data: Record<string, string>; content: string } {
  const match = source.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/)
  if (!match) return { data: {}, content: source }
  const data: Record<string, string> = {}
  for (const rawLine of match[1].split(/\r?\n/)) {
    const line = rawLine.trim()
    if (!line || line.startsWith('#')) continue
    const m = line.match(/^([A-Za-z_][A-Za-z0-9_-]*)\s*:\s*(.*)$/)
    if (!m) continue
    let value = m[2].trim()
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1)
    }
    data[m[1]] = value
  }
  return { data, content: match[2] }
}

export type VariantResources = {
  common: Record<string, unknown>
  lorem: Record<string, unknown>
}

export type CopyVariant = {
  filename: string
  frontmatter: VariantFrontmatter
  resources: VariantResources
  warnings: string[]
}

const ACCEPTED_NAMESPACES = new Set(['common', 'lorem'])

function setNested(target: Record<string, unknown>, path: string[], value: string) {
  let cursor: Record<string, unknown> = target
  for (let i = 0; i < path.length - 1; i++) {
    const seg = path[i]
    const existing = cursor[seg]
    if (typeof existing !== 'object' || existing === null || Array.isArray(existing)) {
      cursor[seg] = {}
    }
    cursor = cursor[seg] as Record<string, unknown>
  }
  cursor[path[path.length - 1]] = value
}

export function parseCopyVariant(source: string, filename = 'unknown'): CopyVariant {
  const parsed = splitFrontmatter(source)
  const fm: VariantFrontmatter = {
    variant: parsed.data.variant ?? '',
    name: parsed.data.name ?? filename,
    description: parsed.data.description,
  }

  const resources: VariantResources = { common: {}, lorem: {} }
  const warnings: string[] = []

  if (!fm.variant) warnings.push(`${filename}: missing "variant" frontmatter (e.g. variant: A)`)

  const lines = parsed.content.split(/\r?\n/)
  let currentKey: string | null = null
  let buffer: string[] = []

  const flush = () => {
    if (currentKey === null) return
    const value = buffer.join('\n').trim()
    if (!value) {
      warnings.push(`${filename}: empty value for key "${currentKey}"`)
      buffer = []
      return
    }
    const segs = currentKey.split('.')
    const ns = segs[0]
    if (!ACCEPTED_NAMESPACES.has(ns)) {
      warnings.push(
        `${filename}: unknown namespace "${ns}" in key "${currentKey}" — must start with "common." or "lorem."`,
      )
      buffer = []
      return
    }
    const rest = segs.slice(1)
    if (rest.length === 0) {
      warnings.push(`${filename}: key "${currentKey}" has no leaf path after namespace`)
      buffer = []
      return
    }
    setNested(resources[ns as 'common' | 'lorem'], rest, value)
    buffer = []
  }

  for (const raw of lines) {
    const h3 = raw.match(/^###\s+(.+?)\s*$/)
    if (h3) {
      flush()
      currentKey = h3[1].trim()
      continue
    }
    // Any other ATX heading (#, ##, ####, …) is a section boundary, not body
    // content — close the current key without opening a new one so heading
    // text never bleeds into a sibling key's body.
    if (/^#{1,2}\s/.test(raw) || /^#{4,6}\s/.test(raw)) {
      flush()
      currentKey = null
      continue
    }
    if (currentKey === null) continue
    buffer.push(raw)
  }
  flush()

  return { filename, frontmatter: fm, resources, warnings }
}
